import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { TCity, TCountry, TExpertiseItem, TProfession, TState } from '@onesource/schemas';
import { FC, ReactNode, useState } from 'react';
import { MdOutlineViewModule, MdPerson } from 'react-icons/md';

import { ProDirectoryProfilePreviewFull } from './profile-preview-full';
import { SearchCardPreviewGrid } from './search-card-preview-grid';
import { normalizePhone, resolveExpertiseNames, TProFormValues } from './steps';

type Props = {
  values: Partial<TProFormValues>;
  countries: TCountry[];
  states: TState[];
  cities: TCity[];
  professions: TProfession[];
  expertiseItems: TExpertiseItem[];
  description?: ReactNode;
  /** Rendered to the left of the "Profile preview" heading (e.g. a "Back to Review" action). */
  headerAction?: ReactNode;
};

type TPreviewView = 'profile' | 'card';

/**
 * Public Pro Directory rendering (profile page + directory card) of a set of form values.
 * Shared by the member's preview step and the admin draft preview.
 */
export const ProDirectoryPreview: FC<Props> = ({
  values,
  countries,
  states,
  cities,
  professions,
  expertiseItems,
  description = (
    <>
      This is how your profile will look in the RYC<sup>®</sup> Professional Directory
    </>
  ),
  headerAction,
}) => {
  const [view, setView] = useState<TPreviewView>('profile');

  const professionOptions = (values.profession ?? []).map(id => ({
    id: Number(id),
    name: professions.find(p => String(p.id) === String(id))?.name ?? id,
  }));
  // Form state holds ids; the preview must show what the public profile will show.
  const expertiseNames = resolveExpertiseNames(values.expertise, expertiseItems);
  const businessCity = cities.find(c => c.id === values.businessCityId)?.name ?? null;
  const businessState = states.find(s => s.id === values.businessStateId)?.name ?? null;
  const businessCountry = countries.find(c => c.id === values.businessCountryId)?.name ?? null;

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          mb: 2,
          position: 'sticky',
          top: 0,
          zIndex: 1,
          bgcolor: 'background.paper',
          py: 1,
          px: 2,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Profile preview
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {description}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ToggleButtonGroup
            value={view}
            exclusive
            size="small"
            color="info"
            onChange={(_, next: TPreviewView | null) => next && setView(next)}
          >
            <ToggleButton value="profile">
              <MdPerson style={{ marginRight: 6 }} />
              Profile view
            </ToggleButton>
            <ToggleButton value="card">
              <MdOutlineViewModule style={{ marginRight: 6 }} />
              Directory View
            </ToggleButton>
          </ToggleButtonGroup>
          {headerAction}
        </Box>
      </Box>

      {/* Mirrors the public profile page's container (teacher-hero-section.tsx on ryc-site): same
          max-width and horizontal padding, so the preview matches what the pro will actually see. */}
      <Container maxWidth={false} sx={{ maxWidth: 1240, px: { xs: '16px', md: '20px' } }}>
        {view === 'profile' ? (
          <ProDirectoryProfilePreviewFull
            data={{
              fullName: values.fullName,
              title: values.title,
              bio: values.bio,
              casualImage: values.casualImage,
              landscapeImage: values.landscapeImage,
              professionNames: professionOptions.map(p => String(p.name)),
              offerings: values.offerings ?? [],
              expertise: expertiseNames,
              languagesTaught: values.languagesTaught ?? [],
              teachingFormat: values.teachingFormat,
              certificates: values.certificates,
              companyName: values.companyName,
              companyEmail: values.companyEmail,
              professionalWebsite: values.professionalWebsite,
              phone: normalizePhone(values.phone),
              businessStreet: values.businessStreet,
              businessCity,
              businessState,
              businessCountry,
              socials: values.socials,
            }}
          />
        ) : (
          <SearchCardPreviewGrid
            data={{
              fullName: values.fullName,
              title: values.title,
              casualImage: values.casualImage,
              profession: professionOptions,
              expertise: expertiseNames.length > 0 ? expertiseNames : undefined,
              businessCity,
              businessState,
              businessCountry,
            }}
          />
        )}
      </Container>
    </Box>
  );
};
